import React from 'react';
import { Dna, Wind, Cpu, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SolutionData {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  description: string;
  features: string[];
  benefits: string[];
  color: string;
}

const Solutions: React.FC = () => {
  const navigate = useNavigate();
  const solutions: SolutionData[] = [
    {
      title: 'CADD',
      subtitle: '计算机辅助药物设计',
      icon: <Dna className="w-8 h-8" />,
      description: '基于ChainCloud云CADD云平台，集成行业内专业解决方案并结合自研高通量工作流引擎技术，致力于骨架跃迁、虚拟筛选、从头设计、QSAP、ADME性质预测等研发环节，加快药物发现进程。',
      features: [
        '分子对接与虚拟筛选',
        'ADME/T性质预测',
        '从头药物设计',
        '先导化合物优化'
      ],
      benefits: [
        '加速药物发现',
        '降低研发成本',
        '提高成功率'
      ],
      color: 'from-emerald-500 to-teal-600'
    },
    {
      title: 'CAE/CFD',
      subtitle: '工程仿真计算',
      icon: <Wind className="w-8 h-8" />,
      description: '基于ChainCloud云CAE/CFD云平台，将常用仿真软件快速构建成Linux/Windows混编的工作流，使模型预处理、仿真计算、后处理等流程自动化、标准化。',
      features: [
        '流体动力学仿真',
        '结构力学分析',
        '传热传质计算',
        '多物理场耦合'
      ],
      benefits: [
        '提升仿真精度',
        '缩短设计周期',
        '优化产品性能'
      ],
      color: 'from-blue-500 to-indigo-600'
    },
    {
      title: 'EDA',
      subtitle: '电子设计自动化',
      icon: <Cpu className="w-8 h-8" />,
      description: '基于ChainCloud云EDA云平台，结合自研计算上云插件和工作流引擎，形成EDA上云专业解决方案，致力于设计与仿真、本地和云上研发流程间的无缝衔接。',
      features: [
        '芯片设计仿真',
        'IC版图设计',
        '时序分析',
        '功耗优化'
      ],
      benefits: [
        '提升设计效率',
        '降低硬件成本',
        '加速产品上市'
      ],
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: '生信分析',
      subtitle: '生物信息学计算',
      icon: <Database className="w-8 h-8" />,
      description: '专为生物信息学研究打造的云计算平台，支持基因测序、蛋白质结构预测、进化分析等多种生物信息学应用。',
      features: [
        '基因组测序分析',
        '蛋白质结构预测',
        '系统发育分析',
        '转录组分析'
      ],
      benefits: [
        '提高分析精度',
        '加速科研进展',
        '降低计算门槛'
      ],
      color: 'from-orange-500 to-red-600'
    }
  ];

  return (
    <section className="spacing-responsive bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container-responsive">
        {/* Header */}
        <div className="text-center mb-16 portrait:mb-10">
          <h2 className="text-responsive-lg font-bold text-gray-900 mb-4 portrait:mb-3">
            行业解决方案
          </h2>
          <p className="text-responsive-sm text-gray-600 max-w-3xl mx-auto">
            针对不同行业需求，提供专业化的云超算解决方案
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 portrait:grid-cols-1 gap-8 portrait:gap-5">
          {solutions.map((solution, index) => (
            <div
              key={index}
              className="group bg-white rounded-3xl portrait:rounded-2xl p-8 portrait:p-5 portrait:md:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              {/* Header */}
              <div className="flex items-center gap-4 portrait:gap-3 mb-6 portrait:mb-4">
                <div className={`w-16 h-16 portrait:w-12 portrait:h-12 portrait:md:w-14 portrait:md:h-14 bg-gradient-to-br ${solution.color} rounded-xl portrait:rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300`}>
                  <div className="portrait:scale-75">
                    {solution.icon}
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl portrait:text-lg portrait:md:text-xl font-bold text-gray-900 mb-1">
                    {solution.title}
                  </h3>
                  <p className="text-gray-600 font-medium text-base portrait:text-sm">
                    {solution.subtitle}
                  </p>
                </div>
              </div>

              {/* Description */}
              <p className="text-gray-700 leading-relaxed mb-6 portrait:mb-4 text-base portrait:text-sm">
                {solution.description}
              </p>

              {/* Features & Benefits */}
              <div className="grid grid-cols-1 md:grid-cols-2 portrait:grid-cols-1 gap-6 portrait:gap-4">
                {/* Features */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 portrait:mb-2 text-base portrait:text-sm">核心功能</h4>
                  <div className="space-y-2 portrait:space-y-1.5">
                    {solution.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm portrait:text-xs text-gray-600">
                        <div className={`w-1.5 h-1.5 portrait:w-1 portrait:h-1 bg-gradient-to-r ${solution.color} rounded-full mr-3 portrait:mr-2`} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Benefits */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 portrait:mb-2 text-base portrait:text-sm">核心价值</h4>
                  <div className="space-y-2 portrait:space-y-1.5">
                    {solution.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center text-sm portrait:text-xs text-gray-600">
                        <div className={`w-1.5 h-1.5 portrait:w-1 portrait:h-1 bg-gradient-to-r ${solution.color} rounded-full mr-3 portrait:mr-2`} />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-6 portrait:mt-4 pt-6 portrait:pt-4 border-t border-gray-100 flex justify-center">
                <button
                  className={`w-auto px-8 portrait:px-6 bg-gradient-to-r ${solution.color} text-white py-3 portrait:py-2.5 rounded-xl portrait:rounded-lg font-medium text-base portrait:text-sm hover:shadow-lg transition-all duration-300 hover:scale-105`}
                  onClick={() => navigate('/console', { state: { from: solution.title } })}
                >
                  了解 {solution.title} 解决方案
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Solutions; 